/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { EnabledPaymentsForOBO } from '@msdyn365-commerce-modules/checkout-utilities';
import { GetCardPaymentAcceptPointInput, getCardPaymentAcceptPointAction, GetCheckoutReturnUrl } from '@msdyn365-commerce-modules/checkout';
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { IGiftCardExtend } from '@msdyn365-commerce/global-state';
import { CardPaymentAcceptPoint, CardPaymentAcceptResult } from '@msdyn365-commerce/retail-proxy';
import classNames from 'classnames';
import { computed, reaction } from 'mobx';
import * as React from 'react';
import { ICheckoutGooglePayData } from './custom-checkout-google-pay.data';
import { IPaymentAuthorizationResult, IPaymentData, IPaymentsError, ITransactionInfo } from './custom-checkout-google-pay.model';
import { ICustomCheckoutGooglePayProps } from './custom-checkout-google-pay.props.autogenerated';
import { triggerDLEvent } from '../../themes/venchi-v2-1/utilities/datalayer';
import { GtmEvents } from '../../themes/venchi-v2-1/utilities/datalayer/types';
import { setCheckoutCartInLocalStorage, setOrderPlacedStatusLocalStorage } from '../../themes/venchi-v2-1/utilities/datalayer/helpers';

export interface ICheckoutGooglePayViewProps extends ICustomCheckoutGooglePayProps<{}> {
    alert?: React.ReactNode;
    className?: string;
    checkoutButton?: HTMLDivElement;
    checkoutGooglePay: IModuleProps;
}

export interface ICheckoutGooglePayModuleProps extends ICustomCheckoutGooglePayProps<ICheckoutGooglePayData> {}

export enum GooglePayButtonState {
    Unknown,
    NotAvailable,
    Available
}

interface IMerchantConfiguration {
    adyenMerchantId?: string;
    googleMerchantId?: string;
    currencyCode?: string;
    environment?: string;
    connectorId?: string;
}

interface ICheckoutGooglePayState {
    buttonState: GooglePayButtonState;
    merchantConfiguration?: IMerchantConfiguration;
    errorMessage?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;

/**
 *
 * CheckoutGooglePay component
 * @extends {React.Component<ICheckoutGooglePayModuleProps, ICheckoutGooglePayState>}
 */
export class CheckoutGooglePay extends React.Component<ICheckoutGooglePayModuleProps, ICheckoutGooglePayState> {
    private readonly moduleClassName: string = 'ms-checkout-google-pay';
    private readonly zeroAmount: number = 0;

    private readonly baseRequest = {
        apiVersion: 2,
        apiVersionMinor: 0
    };

    private readonly allowedCardNetworks = ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'];
    private readonly allowedCardAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];

    private readonly baseCardPaymentMethod = {
        type: 'CARD',
        parameters: {
            allowedAuthMethods: this.allowedCardAuthMethods,
            allowedCardNetworks: this.allowedCardNetworks
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private paymentsClient: any = null;
    private googlePayButton: HTMLDivElement = document.createElement('div');

    @computed private get getLoyaltyAmount(): number {
        const {
            data: { checkout }
        } = this.props;
        const checkoutState = checkout?.result;
        return checkoutState?.loyaltyAmount ? checkoutState.loyaltyAmount : this.zeroAmount;
    }

    @computed private get getGiftCardTotalAmount(): number {
        const {
            data: { checkout }
        } = this.props;
        const checkoutState = checkout?.result;
        if (!checkoutState || !checkoutState.giftCardExtends) {
            return this.zeroAmount;
        }
        return checkoutState.giftCardExtends.reduce((count: number, giftCard: IGiftCardExtend) => {
            return count + (giftCard.Balance || this.zeroAmount);
        }, 0);
    }

    @computed private get getCustomerAccountAmount(): number {
        const {
            data: { checkout }
        } = this.props;
        const checkoutState = checkout?.result;

        return checkoutState?.customerAccountAmount ?? this.zeroAmount;
    }

    @computed private get amountDue(): number {
        const {
            data: { checkout }
        } = this.props;

        const cart = checkout?.result?.checkoutCart.cart;
        if (!cart || !cart.AmountDue) {
            return this.zeroAmount;
        }

        return Math.max(
            this.zeroAmount,
            cart.AmountDue - this.getGiftCardTotalAmount - this.getLoyaltyAmount - this.getCustomerAccountAmount
        );
    }

    @computed private get googleClient() {
        if (this.paymentsClient === null || this.paymentsClient === undefined) {
            const { merchantConfiguration } = this.state;
            const clientRequest = {
                environment: merchantConfiguration?.environment,
                paymentDataCallbacks: {
                    onPaymentAuthorized: this.onPaymentAuthorized
                }
            };

            this.paymentsClient = new window.google.payments.api.PaymentsClient(clientRequest);
        }

        return this.paymentsClient;
    }

    @computed private get transactionInfo(): ITransactionInfo {
        const { merchantConfiguration } = this.state;
        return {
            currencyCode: merchantConfiguration?.currencyCode || '',
            totalPriceStatus: 'ESTIMATED',
            totalPrice: `${this.amountDue}`
        };
    }

    @computed private get paymentDataRequest() {
        const { merchantConfiguration } = this.state;
        const cardPaymentMethod = {
            ...this.baseCardPaymentMethod,
            tokenizationSpecification: {
                type: 'PAYMENT_GATEWAY',
                parameters: {
                    gateway: 'adyen',
                    gatewayMerchantId: merchantConfiguration?.adyenMerchantId || ''
                }
            }
        };

        const callbackIntents: string[] = ['PAYMENT_AUTHORIZATION'];
        const paymentDataRequest = {
            ...this.baseRequest,
            allowedPaymentMethods: [cardPaymentMethod],
            transactionInfo: this.transactionInfo,
            merchantInfo: {
                merchantId: merchantConfiguration?.googleMerchantId || ''
            },
            callbackIntents: callbackIntents
        };

        return paymentDataRequest;
    }

    public constructor(props: ICustomCheckoutGooglePayProps<ICheckoutGooglePayData>) {
        super(props);
        this.state = {
            buttonState: GooglePayButtonState.Unknown
        };
    }

    public async componentDidMount() {
        const {
            data: { checkout }
        } = this.props;

        reaction(
            () => checkout?.result?.redirectAdditionalContext && checkout?.result?.redirectPaymentConnectorId,
            async () => this.performRedirect()
        );

        reaction(
            () => checkout?.result?.isPaymentVerificationRedirection && checkout?.result?.isPlaceOrderEnabledInRedirection,
            async () => this.handlePaymentVerification()
        );

        await this.loadPaymentAcceptPage();
    }

    public render(): JSX.Element | null {
        const {
            config: { className },
            telemetry
        } = this.props;

        if (this.props.context.request.user.isOBORequest && EnabledPaymentsForOBO.ExpressPayment === 0) {
            telemetry.information('isOBORequest is enabled, google pay module wont render');
            return null;
        }

        let checkoutButton: HTMLDivElement;
        switch (this.state.buttonState) {
            case GooglePayButtonState.Available:
                checkoutButton = this.googlePayButton;
                break;

            case GooglePayButtonState.NotAvailable:
            case GooglePayButtonState.Unknown:
            default:
                checkoutButton = document.createElement('div');
        }

        const viewProps: ICheckoutGooglePayViewProps = {
            ...this.props,
            ...this.state,
            className,
            checkoutGooglePay: {
                moduleProps: this.props,
                className: classNames(this.moduleClassName, className)
            },
            checkoutButton
        };

        return this.props.renderView(viewProps);
    }

    private async loadPaymentAcceptPage() {
        const {
            config,
            context: {
                actionContext,
                request: { apiSettings }
            },
            telemetry
        } = this.props;

        const input = {
            amountDue: this.amountDue,
            apiSettings,
            paymenTenderType: config.paymentTenderType
        };

        await getCardPaymentAcceptPointAction(new GetCardPaymentAcceptPointInput(input), actionContext)
            .then((paymentAcceptPoint: any) => {
                const configuration = this.getMerchantConfiguration(paymentAcceptPoint);
                if (configuration) {
                    this.setState({ merchantConfiguration: configuration });
                    this.initialize();
                } else {
                    telemetry.error('Unable to initialize Google Pay module');
                    this.setState({ buttonState: GooglePayButtonState.NotAvailable });
                }
            })
            .catch((error: Error) => {
                telemetry.error(error.message);
                console.error(
                    'Error in custom-checkout-google-pay.tsx - CheckoutGooglePay: getCardPaymentAcceptPointAction failed:',
                    error.message
                );
            });
    }

    private getMerchantConfiguration(paymentAcceptPoint: CardPaymentAcceptPoint): IMerchantConfiguration | null {
        const { telemetry } = this.props;

        try {
            const configuration: IMerchantConfiguration = JSON.parse(paymentAcceptPoint?.AcceptPageContent || '');
            configuration.connectorId = paymentAcceptPoint?.PaymentConnectorId;

            if (StringExtensions.isNullOrWhitespace(configuration.adyenMerchantId)) {
                throw new Error('missing Adyen merchant account identifier');
            } else if (StringExtensions.isNullOrWhitespace(configuration.currencyCode)) {
                throw new Error('missing currency code');
            } else if (StringExtensions.isNullOrWhitespace(configuration.connectorId)) {
                throw new Error('missing connector identifier');
            } else if (StringExtensions.isNullOrWhitespace(configuration.environment)) {
                throw new Error('missing environment property');
            } else if (configuration.environment === 'PRODUCTION' && StringExtensions.isNullOrWhitespace(configuration.googleMerchantId)) {
                throw new Error('missing Google merchant account identifier');
            }

            return configuration;
        } catch (error) {
            telemetry.error(`Caught exception during parsing configuration: ${error}`);
            console.error(
                'Error in custom-checkout-google-pay.tsx - CheckoutGooglePay: Caught exception during parsing configuration:',
                error
            );
        }

        return null;
    }

    private initialize = () => {
        const { telemetry } = this.props;

        const script = document.createElement('script');
        script.onload = this.loadGoogleSdk;
        script.src = 'https://pay.google.com/gp/p/js/pay.js';
        script.async = true;
        script.onerror = () => telemetry.error('unable to load Google Pay SDK');
        document.body.appendChild(script);
    };

    /**
     * Load Google Pay JS SDK.
     */
    private loadGoogleSdk = async () => {
        const { telemetry } = this.props;
        const isReadyToPayRequest = {
            ...this.baseRequest,
            allowedPaymentMethods: [this.baseCardPaymentMethod]
        };

        let buttonState: GooglePayButtonState = GooglePayButtonState.NotAvailable;
        try {
            const isReadyToPay = await this.googleClient.isReadyToPay(isReadyToPayRequest);
            if (isReadyToPay?.result) {
                this.googlePayButton = this.googleClient.createButton({
                    allowedPaymentMethods: [this.baseCardPaymentMethod],
                    buttonColor: 'default',
                    buttonType: 'buy',
                    buttonSizeMode: 'fill',
                    buttonLocale: this.props.context.actionContext.requestContext.locale.split('-')[0] || 'en',
                    onClick: this.handlePayButton
                });

                buttonState = GooglePayButtonState.Available;
            } else {
                telemetry.error('[loadGoogleSdk] current browser does not support Google Pay');
            }
        } catch (error) {
            telemetry.error(`[loadGoogleSdk] unexpected error during loading sdk: ${error}`);
        }

        return this.setState({
            buttonState
        });
    };

    /**
     * Handler for Google Pay button.
     */
    private handlePayButton = () => {
        const { telemetry } = this.props;
        this.googleClient.loadPaymentData(this.paymentDataRequest).catch((error: IPaymentsError) => {
            if (error.statusCode === 'CANCELED') {
                triggerDLEvent({
                    event: GtmEvents.CHECKOUT_GOOGLE_PAY_CANCEL
                });
                telemetry.information('[CheckoutGooglePay::handlePayButton] popup was canceled');
            } else {
                triggerDLEvent({
                    event: GtmEvents.CHECKOUT_GOOGLE_PAY_ERROR
                });
                telemetry.error(
                    `[CheckoutGooglePay::handlePayButton] unable to complete Google transaction (${error.statusCode}): ${error.statusMessage}`
                );
                console.error(
                    'Error in custom-checkout-google-pay.tsx - CheckoutGooglePay: unable to complete Google transaction:',
                    error.statusMessage
                );
            }
        });
    };

    private onPaymentAuthorized = async (paymentData: IPaymentData) => {
        const { resources } = this.props;
        let result: IPaymentAuthorizationResult;
        try {
            await this.processPayment(paymentData);

            setOrderPlacedStatusLocalStorage(true);
            setCheckoutCartInLocalStorage(this.props.data.checkout?.result?.checkoutCart.cart);

            result = {
                transactionState: 'SUCCESS'
            };
            triggerDLEvent({
                event: GtmEvents.CHECKOUT_GOOGLE_PAY_SUCCESS
            });
        } catch (e) {
            result = {
                transactionState: 'ERROR',
                error: {
                    reason: 'PAYMENT_DATA_INVALID',
                    message: resources.genericErrorMessage,
                    intent: 'PAYMENT_AUTHORIZATION'
                }
            };
            triggerDLEvent({
                event: GtmEvents.CHECKOUT_GOOGLE_PAY_ERROR
            });
        }

        return new Promise((resolve, reject) => {
            resolve(result);
        });
    };

    /**
     * Process payment data in CSU.
     * @param paymentData The payment data from Google SDK.
     */
    private processPayment = async (paymentData: IPaymentData) => {
        const {
            data: { checkout },
            context: { actionContext }
        } = this.props;
        const { merchantConfiguration } = this.state;
        const paymentConnectorId: string = merchantConfiguration?.connectorId || '';
        const message = {
            paymentToken: paymentData.paymentMethodData.tokenizationData.token,
            browserInfo: {
                acceptHeader: '*/*',
                colorDepth: window.screen.colorDepth,
                language: window.navigator.language,
                javaEnabled: window.navigator.javaEnabled(),
                screenHeight: window.screen.height,
                screenWidth: window.screen.width,
                userAgent: window.navigator.userAgent,
                timeZoneOffset: new Date().getTimezoneOffset()
            }
        };

        const settings = {
            ReturnUrl: GetCheckoutReturnUrl(actionContext),
            ...(paymentConnectorId && { PaymentConnectorId: paymentConnectorId })
        };

        const cardPaymentAcceptResult: CardPaymentAcceptResult = {
            AccessCode: btoa(JSON.stringify(message)),
            Settings: settings
        };

        await checkout?.result?.updateCardPaymentAcceptResult({ newCardPaymentAcceptResult: cardPaymentAcceptResult });
        await checkout?.result?.updateIsPaymentSectionContainerReady({ newIsPaymentSectionContainerReady: true });
    };

    /**
     * Perform redirect
     */
    private performRedirect = async () => {
        const {
            config,
            data: { checkout },
            telemetry
        } = this.props;

        const { merchantConfiguration } = this.state;
        const checkoutState = checkout?.result;

        if (
            !StringExtensions.isNullOrWhitespace(checkoutState?.redirectPaymentConnectorId) &&
            checkoutState?.redirectPaymentConnectorId === merchantConfiguration?.connectorId
        ) {
            let redirectInfo;
            try {
                redirectInfo = JSON.parse(checkoutState?.redirectAdditionalContext || '');
            } catch (error) {
                telemetry.error(`[CheckoutGooglePay::performRedirect] unable to parse additional context`);
                console.error('Error in custom-checkout-google-pay.tsx - CheckoutGooglePay: unable to parse additional context');
                return;
            }

            await checkoutState?.updatePaymentTenderType({ newPaymentTenderType: config.paymentTenderType });
            await checkoutState?.saveDataInStorage({});
            await checkoutState?.updateIsPaymentVerificationRequired({ newIsPaymentVerificationRequired: true });

            const { url, data } = redirectInfo;
            const form = document.createElement('form');

            form.method = 'POST';
            form.action = url;

            for (const propertyName of Object.keys(data)) {
                const element = document.createElement('input');
                element.name = propertyName;
                element.value = data[propertyName];
                form.appendChild(element);
            }

            document.body.appendChild(form);
            form.submit();
        } else {
            telemetry.warning(`[CheckoutGooglePay::performRedirect] cannot perform redirect, connector id is not valid or matching`);
        }
    };

    /**
     * Handle redirect shopper bank institution.
     */
    private handlePaymentVerification = async () => {
        const {
            config,
            context: { actionContext },
            data: { checkout },
            telemetry
        } = this.props;

        const { merchantConfiguration } = this.state;
        const checkoutState = checkout?.result;

        if (
            !StringExtensions.isNullOrWhitespace(checkoutState?.paymentTenderType) &&
            checkoutState?.paymentTenderType === config.paymentTenderType
        ) {
            const { requestFormData, query } = this.props.context.request;
            const formData = btoa(JSON.stringify(requestFormData ?? { redirectResult: query?.redirectResult }));

            const settings = {
                ReturnUrl: GetCheckoutReturnUrl(actionContext),
                PaymentConnectorId: merchantConfiguration?.connectorId
            };

            const cardPaymentAcceptResult: CardPaymentAcceptResult = {
                AccessCode: formData,
                Settings: settings
            };

            await checkoutState?.updateCardPaymentAcceptResult({ newCardPaymentAcceptResult: cardPaymentAcceptResult });
            await checkoutState?.checkoutCart.refreshCart({});
            await checkoutState?.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
            await checkoutState?.updateIsPaymentSectionContainerReady({ newIsPaymentSectionContainerReady: true });
        } else {
            telemetry.warning(
                `[CheckoutGooglePay::handlePaymentVerification] ignore payment verification, checkout tender type: '${checkoutState?.paymentTenderType}', config tender type: '${config.paymentTenderType}'`
            );
        }
    };
}

export default CheckoutGooglePay;
